<script setup lang="ts">
/**
 * FighterPanel — Fighter management panel for carriers/supercarriers
 * Shows fighter tubes, squadron sizes, light/support/heavy type badges.
 */
import { computed } from 'vue';
import { useFitStore } from '../../stores/fitStore';
import { iconUrl } from '../../composables/useApi';
import type { FitFighter } from '../../../shared/types';

const store = useFitStore();

const fit = computed(() => store.activeFit);
const fighters = computed<FitFighter[]>(() => fit.value?.fighters ?? []);

const tubeCount = computed(() => fit.value?.fighterTubes ?? 0);
const lightSlots = computed(() => fit.value?.fighterLightSlots ?? 0);
const supportSlots = computed(() => fit.value?.fighterSupportSlots ?? 0);
const heavySlots = computed(() => fit.value?.fighterHeavySlots ?? 0);

function fighterTypeBadge(f: FitFighter): string {
  if (f.isLight) return 'Light';
  if (f.isSupport) return 'Support';
  if (f.isHeavy) return 'Heavy';
  return '';
}

function fighterTypeBadgeClass(f: FitFighter): string {
  if (f.isLight) return 'badge-light';
  if (f.isSupport) return 'badge-support';
  if (f.isHeavy) return 'badge-heavy';
  return '';
}

function removeFighter(f: FitFighter): void {
  store.removeFighterFromFit(f.id);
}

function toggleFighterActive(f: FitFighter): void {
  store.toggleFighterActive(f.id, !f.isActive);
}

function handleDrop(e: DragEvent): void {
  e.preventDefault();
  const data = e.dataTransfer?.getData('text/plain');
  if (!data) return;
  try {
    const parsed = JSON.parse(data);
    if (parsed.typeID) {
      // Find next available tube index
      const usedTubes = new Set(fighters.value.map(f => f.tubeIndex));
      let nextTube = 0;
      while (usedTubes.has(nextTube) && nextTube < tubeCount.value) nextTube++;
      if (nextTube < tubeCount.value) {
        store.addFighterToFit(parsed.typeID, parsed.typeName || 'Fighter', nextTube, 1);
      } else {
        store.addToast('warning', 'All fighter tubes are occupied');
      }
    }
  } catch { /* ignore bad drag data */ }
}

function handleDragOver(e: DragEvent): void {
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
}
</script>

<template>
  <div class="fighter-panel" @drop="handleDrop" @dragover="handleDragOver">
    <!-- Stats bar -->
    <div class="fighter-stats-bar">
      <span class="fighter-stat">Tubes: {{ fighters.length }}/{{ tubeCount }}</span>
      <span class="fighter-stat">Light: {{ lightSlots }}</span>
      <span class="fighter-stat">Support: {{ supportSlots }}</span>
      <span class="fighter-stat">Heavy: {{ heavySlots }}</span>
    </div>

    <!-- Fighter list -->
    <div v-if="fighters.length > 0" class="fighter-list">
      <div
        v-for="fighter in fighters"
        :key="fighter.id"
        class="fighter-row"
        :class="{ inactive: !fighter.isActive }"
      >
        <img
          :src="iconUrl(fighter.typeID, 32)"
          class="fighter-icon clickable"
          :alt="fighter.typeName"
          :title="fighter.isActive ? 'Click to deactivate' : 'Click to activate'"
          @click.stop="toggleFighterActive(fighter)"
        />
        <div class="fighter-info">
          <span class="fighter-name">{{ fighter.typeName }}</span>
          <span class="fighter-meta">
            Tube {{ fighter.tubeIndex + 1 }}
            &middot; {{ fighter.squadronSize }}/{{ fighter.maxSquadronSize }}
          </span>
        </div>
        <span
          v-if="fighterTypeBadge(fighter)"
          class="fighter-badge"
          :class="fighterTypeBadgeClass(fighter)"
        >
          {{ fighterTypeBadge(fighter) }}
        </span>
        <button class="fighter-remove" @click="removeFighter(fighter)" title="Remove fighter">&times;</button>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="fighter-empty">
      <span>Drag fighters from the Market browser</span>
    </div>
  </div>
</template>

<style scoped>
.fighter-panel {
  display: flex;
  flex-direction: column;
  min-height: 80px;
}

.fighter-stats-bar {
  display: flex;
  gap: 12px;
  padding: 4px 8px;
  font-size: 0.7rem;
  color: var(--text-secondary);
  border-bottom: 1px solid rgba(42, 63, 95, 0.3);
}

.fighter-list {
  display: flex;
  flex-direction: column;
}

.fighter-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-bottom: 1px solid rgba(42, 63, 95, 0.2);
  transition: background var(--transition-fast);
}

.fighter-row:hover {
  background: var(--bg-hover);
}

.fighter-row.inactive {
  opacity: 0.5;
}

.fighter-icon {
  width: 24px;
  height: 24px;
  border-radius: 2px;
  flex-shrink: 0;
}

.fighter-icon.clickable {
  cursor: pointer;
  transition: opacity var(--transition-fast);
}

.fighter-icon.clickable:hover {
  opacity: 0.7;
}

.fighter-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.fighter-name {
  font-size: 0.78rem;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fighter-meta {
  font-size: 0.65rem;
  color: var(--text-secondary);
}

.fighter-badge {
  font-size: 0.6rem;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 6px;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.badge-light {
  background: rgba(74, 158, 255, 0.15);
  color: var(--accent-blue);
}

.badge-support {
  background: rgba(76, 175, 80, 0.15);
  color: var(--accent-green);
}

.badge-heavy {
  background: rgba(255, 152, 0, 0.15);
  color: var(--accent-orange);
}

.fighter-remove {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1rem;
  cursor: pointer;
  padding: 0 4px;
  opacity: 0;
  transition: opacity var(--transition-fast), color var(--transition-fast);
}

.fighter-row:hover .fighter-remove {
  opacity: 1;
}

.fighter-remove:hover {
  color: var(--accent-red);
}

.fighter-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: var(--text-secondary);
  font-size: 0.75rem;
}
</style>
